// versionCheck — B-1b (MONIT 2026-09-12_1750, F3): kontrola wersji przy otwarciu i powrocie.
//
// Skorupa zna swoją wersję (APP_VERSION z builda). Przy starcie, przy powrocie do aplikacji
// (visibilitychange → visible), przy zmianie kontrolera SW i przy zdarzeniach instalacji
// (updatefound → statechange) pyta: (1) SW (kontroler / installing / waiting: wersja + precache
// n/N), (2) serwer — przez puls floty (odpowiedź niesie latest + min_required; puls sam
// pilnuje limitu 10 min) albo GET /api/version, gdy nie ma sesji. Brak sieci = pomiń, bez błędu.
// Tryb lokalny: pyta TYLKO SW (zero sieci); stany (b) i (d) działają, bo pliki są lokalnie
// (min_required z ostatniej odpowiedzi serwera w IDB).
//
// Wynik = evaluateVersionState (src/shared/appVersion.js) → subskrybenci (VersionBanner,
// Serwis LITE/pełny). „Przeładuj" = reloadForNewVersion(): SKIP_WAITING do waiting SW (jeśli
// jest) → controllerchange → location.reload(). Stan (d) blokuje ekran, ale przeładowanie
// działa bez internetu (shell i chunki z cache nowej wersji).

import { fetchSwRegistrationInfo, querySwWorker } from '../components/supportDiagnostics.js';
import { apiFetch } from '../hooks/useApi.js';
import { getToken } from '../hooks/useAuth.js';
import { isLocalMode } from './localMode.js';
import { getSetting, setSetting } from './offlineDB.js';
import {
  APP_VERSION, evaluateVersionState, isBannerDismissed, compareVersions,
} from '../shared/appVersion.js';
import {
  sendHeartbeat, getServerInfo, noteVersionMismatch, noteSwInstallFailure, noteSwInstallSuccess,
  installLocalModePulse,
} from './fleet.js';

export const KEY_BANNER_DISMISSED_AT = 'version_banner_dismissed_at';
const SERVER_INFO_STALE_MS = 10 * 60 * 1000;

let current = evaluateVersionState({ shell: APP_VERSION });
let sw = { active: null, installing: null, waiting: null, hasRegistration: false };
let dismissedAt = 0;
let checking = null;
let initialized = false;
// Puls „sw-installed" raz per wersja aktywnego SW (statechange installed/activated + controllerchange
// odpalają się seriami przy jednej instalacji; kolejne = zwykły limit 10 min).
let lastInstalledPulse = null;
const listeners = new Set();

function emit() {
  for (const fn of listeners) {
    try { fn(current); } catch { /* nic */ }
  }
}

export function subscribeVersionState(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getVersionState() { return current; }
export function getSwSnapshot() { return sw; }
export function getBannerDismissedAt() { return dismissedAt; }

/** Zamknięcie banera (b): wraca po 1 h (DN3 = a). */
export async function dismissBanner() {
  dismissedAt = Date.now();
  await setSetting(KEY_BANNER_DISMISSED_AT, dismissedAt).catch(() => {});
  emit();
}

export function isBannerHidden(now = Date.now()) {
  return isBannerDismissed(dismissedAt, now);
}

async function fetchServerInfoNoAuth() {
  try {
    const r = await apiFetch('GET', '/api/version', null, { skipAuth: true, timeoutMs: 6000 });
    return { latest: r?.latest || null, minRequired: r?.min_required || null, at: Date.now() };
  } catch {
    return null;
  }
}

/**
 * Pełna kontrola: SW + serwer → stan. `reason` steruje pulsem (start/visible/sw-installed/manual).
 * Nigdy nie rzuca. Współbieżne wywołania łączone.
 */
export async function runVersionCheck(reason = 'manual') {
  if (checking) return checking;
  checking = (async () => {
    try {
      sw = await fetchSwRegistrationInfo();
      let server = getServerInfo();
      const online = typeof navigator === 'undefined' || navigator.onLine !== false;
      let pulseReason = reason;
      if (reason === 'sw-installed') {
        const v = sw.active?.version || null;
        if (v && v === lastInstalledPulse) pulseReason = 'sw-installed-again';
        else if (v) lastInstalledPulse = v;
      }
      if (online && !isLocalMode()) {
        if (getToken()) {
          const res = await sendHeartbeat({ reason: pulseReason, sw });
          if (res) server = { latest: res.latest || null, minRequired: res.minRequired || null, at: Date.now() };
          else server = getServerInfo() || server;
        } else if (!server || Date.now() - (server.at || 0) > SERVER_INFO_STALE_MS) {
          const r = await fetchServerInfoNoAuth();
          if (r) server = r;
        }
      }
      current = evaluateVersionState({
        shell: APP_VERSION,
        active: sw.active,
        installing: sw.installing,
        waiting: sw.waiting,
        latest: server?.latest || null,
        minRequired: server?.minRequired || null,
      });
      emit();
      // F5 (1): pierwsza niezgodność wersji dla pary (skorupa, latest) → diagnostyka.
      if (server?.latest && compareVersions(server.latest, APP_VERSION) > 0 && APP_VERSION !== 'dev') {
        noteVersionMismatch(APP_VERSION, server.latest).catch(() => {});
      }
    } catch {
      /* stan bez zmian */
    } finally {
      checking = null;
    }
    return current;
  })();
  return checking;
}

/**
 * „Przeładuj": jeśli jest waiting SW → SKIP_WAITING i czekamy na controllerchange (max 3 s),
 * potem location.reload(). Bez waiting: sam reload (nowy SW już kontroluje stronę).
 */
export async function reloadForNewVersion() {
  try {
    const reg = navigator.serviceWorker?.getRegistration ? await navigator.serviceWorker.getRegistration() : null;
    if (reg?.waiting) {
      await new Promise((resolve) => {
        const t = setTimeout(resolve, 3000);
        navigator.serviceWorker.addEventListener('controllerchange', () => { clearTimeout(t); resolve(); }, { once: true });
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      });
    }
  } catch { /* nic */ }
  try { window.location.reload(); } catch { /* nic */ }
}

/**
 * Start: listenery (visible, controllerchange, updatefound → statechange), puls przy
 * przełączeniu Trybu lokalnego, pierwsza kontrola. Idempotentne.
 */
export async function initVersionCheck() {
  if (initialized || typeof window === 'undefined') return false;
  initialized = true;
  try { dismissedAt = Number(await getSetting(KEY_BANNER_DISMISSED_AT)) || 0; } catch { dismissedAt = 0; }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') runVersionCheck('visible').catch(() => {});
  });

  installLocalModePulse();

  const swc = navigator.serviceWorker;
  if (swc) {
    swc.addEventListener('controllerchange', () => runVersionCheck('sw-installed').catch(() => {}));
    swc.getRegistration?.().then((reg) => {
      if (!reg) return;
      const watchInstalling = (worker) => {
        if (!worker) return;
        let installed = false;
        let version = null;
        querySwWorker(worker, 1500).then((i) => { version = i?.version || null; }).catch(() => {});
        runVersionCheck('updatefound').catch(() => {});
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed') { installed = true; noteSwInstallSuccess().catch(() => {}); runVersionCheck('sw-installed').catch(() => {}); }
          else if (worker.state === 'activated') runVersionCheck('sw-installed').catch(() => {});
          else if (worker.state === 'redundant' && !installed) {
            // Install padł (precache niepełny) — licznik; po 3 → diagnostyka (F5 wyzwalacz 2).
            noteSwInstallFailure(version).catch(() => {});
            runVersionCheck('sw-install-failed').catch(() => {});
          }
        });
      };
      if (reg.installing) watchInstalling(reg.installing);
      reg.addEventListener('updatefound', () => watchInstalling(reg.installing));
    }).catch(() => {});
  }

  // Hak diagnostyczny (harness S12/S13 + devtools u Martina): ręczna kontrola wersji + puls.
  try { window.__foVersionCheck = (reason) => runVersionCheck(reason || 'manual'); } catch { /* nic */ }

  await runVersionCheck('start');
  return true;
}

/** Test hook. */
export function __resetVersionCheckForTest() {
  current = evaluateVersionState({ shell: APP_VERSION });
  sw = { active: null, installing: null, waiting: null, hasRegistration: false };
  dismissedAt = 0; checking = null; initialized = false; listeners.clear(); lastInstalledPulse = null;
}
